"use client";

import { RiArrowLeftLine } from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import { Card, CardContent } from "@workspace/ui/components/card";
import { Separator } from "@workspace/ui/components/separator";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, Suspense } from "react";
import { ArtistProfileCard, SpotifyEmbedCard } from "@/components/features/artist/cards";
import { GroupMembersRow } from "@/components/features/group/cards/group-members-row";
import { GroupContent } from "@/components/features/group/content/group-content";
import { GroupProfileSkeleton } from "@/components/features/group/skeletons";
import { trackGroupDiscovery } from "@/lib/services/analytics";
import { trpc } from "@/lib/trpc/react";

interface GroupProfilePageClientProps {
	groupSlug: string;
}

function GroupProfilePageClientInner({
	groupSlug,
}: GroupProfilePageClientProps) {
	const router = useRouter();
	const searchParams = useSearchParams();

	// Fetch group data with members
	const {
		data: profileData,
		isLoading,
		error,
	} = trpc.group.getProfile.useQuery(
		{
			slug: groupSlug,
		},
		{
			retry: false,
		},
	);

	// Fetch stats - must be called before any conditional returns
	const { data: statsData } = trpc.group.getStats.useQuery({ slug: groupSlug });

	// Track group view on mount when we have group data
	useEffect(() => {
		if (profileData?.group) {
			// Determine the source based on the referrer
			const fromParam = searchParams?.get("from");
			let source: "festival" | "search" | "discover" | "profile" = "discover";

			if (fromParam === "search") {
				source = "search";
			} else if (fromParam === "festival") {
				source = "festival";
			} else if (fromParam === "profile") {
				source = "profile";
			}

			trackGroupDiscovery(
				profileData.group.id,
				profileData.group.name,
				source,
				"viewed",
			);
		}
	}, [profileData?.group, searchParams]);

	const handleBack = useCallback(() => {
		const fromParam = searchParams?.get("from");
		if (fromParam === "search" || window.history.length > 1) {
			router.back();
		} else {
			router.push("/artists");
		}
	}, [router, searchParams]);

	if (isLoading) {
		return <GroupProfileSkeleton />;
	}

	if (error || !profileData?.group) {
		return (
			<div className="container mx-auto max-w-3xl px-4 py-4">
				<div className="flex flex-col gap-3">
					<div className="flex items-center gap-2">
						<Button
							variant="secondary"
							size="sm"
							onClick={handleBack}
							className="flex items-center gap-1 text-sm"
						>
							<RiArrowLeftLine className="h-4 w-4" />
							<span>Back</span>
						</Button>
					</div>

					<Card className="border bg-card shadow-sm">
						<CardContent className="py-8 text-center">
							<h1 className="mb-2 font-bold text-2xl">Group Not Found</h1>
							<p className="mb-4 text-muted-foreground">
								The requested group could not be found or may have been removed.
							</p>
							<Button asChild>
								<Link href="/artists">Browse Artists</Link>
							</Button>
						</CardContent>
					</Card>
				</div>
			</div>
		);
	}

	const { group, members } = profileData;
	const primaryArtist = members?.[0];

	// Transform group data to match ArtistProfileCard expectations
	// Prioritize group Spotify data over primary artist data
	const groupAsArtist = {
		artist_id: group.id,
		artist_name: group.name,
		artist_slug: group.slug,
		artist_image_url: group.imageUrl || primaryArtist?.artist_image_url || null,
		artist_bio: null,
		spotify_genres:
			group.spotifyGenres || primaryArtist?.artist_spotify_genres || null,
		manual_genres: primaryArtist?.artist_manual_genres || null,
		website_url: null,
		instagram_url: null,
		twitter_url: null,
		facebook_url: null,
		tiktok_url: null,
		soundcloud_url: null,
		apple_music_url: null,
		spotify_url: group.spotifyUrl || null,
		youtube_url: null,
		store_url: null,
		total_sets: statsData?.total_sets || 0,
		festivals_played: statsData?.total_festivals || 0,
		total_shows: statsData?.total_shows || 0,
		total_reviewers: 0,
		average_rating: null,
	};

	return (
		<div className="container mx-auto max-w-3xl px-4 py-4">
			<div className="flex flex-col gap-3">
				{/* Navigation */}
				<div className="flex items-center gap-2">
					<Button
						variant="secondary"
						size="sm"
						onClick={handleBack}
						className="flex items-center gap-1 text-sm"
					>
						<RiArrowLeftLine className="h-4 w-4" />
						<span>Back</span>
					</Button>
					<Separator className="h-4" orientation="vertical" />
					<span className="font-medium text-sm">{group.name}</span>
				</div>

				{/* Group Profile Card - using the exact same component as artist page */}
				<ArtistProfileCard artist={groupAsArtist} priority={true} />

				{/* Group Members Row - custom component showing member cards */}
				{members && members.length > 0 && <GroupMembersRow members={members} />}

				{/* Spotify Embed Players for each member */}
				{members && members.length > 0 && members.some(m => m.artist_spotify_id) && (
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
						{members.map((member) =>
							member.artist_spotify_id ? (
								<SpotifyEmbedCard
									key={member.artist_id}
									spotifyId={member.artist_spotify_id}
									artistName={member.artist_name}
								/>
							) : null
						)}
					</div>
				)}

				{/* Group Content - festivals/sets tabs with filters */}
				<GroupContent groupSlug={groupSlug} groupName={group.name} />
			</div>
		</div>
	);
}

export function GroupProfilePageClient(props: GroupProfilePageClientProps) {
	return (
		<Suspense fallback={<GroupProfileSkeleton />}>
			<GroupProfilePageClientInner {...props} />
		</Suspense>
	);
}
